import { useCallback, useEffect, useState } from "react";
import { Admin, Login, Logout } from "./components/Admin";
import { Privacy } from "./components/Privacy";
import { PublicSite } from "./components/PublicSite";
import { ToolsDownload } from "./components/ToolsDownload";
import type { SessionState } from "./services/api";
import { getMe } from "./services/api";

const clientRoutedPaths = new Set(["/", "/blog", "/privacy", "/tools", "/visualizer"]);

function normalizeClientPath(pathname: string) {
  return pathname.toLowerCase().replace(/\/+$/, "") || "/";
}

function getClientRoute(url: URL) {
  if (url.origin !== window.location.origin) {
    return null;
  }

  const normalized = normalizeClientPath(url.pathname);
  return clientRoutedPaths.has(normalized) ? normalized : null;
}

function usePathname() {
  const [pathname, setPathname] = useState(() => window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.target || anchor.hasAttribute("download")) {
        return;
      }

      const route = getClientRoute(new URL(anchor.href));
      if (!route) {
        return;
      }

      event.preventDefault();
      if (window.location.pathname !== route) {
        window.history.pushState({}, "", route);
        setPathname(route);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("popstate", handlePopState);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return pathname;
}

export function App() {
  const pathname = usePathname();
  const normalizedPathname = normalizeClientPath(pathname);
  const [session, setSession] = useState<SessionState | null>(null);
  const [sessionError, setSessionError] = useState("");
  const needsSession = pathname === "/admin" || pathname === "/logout";

  const refreshSession = useCallback(async () => {
    try {
      setSession(await getMe());
      setSessionError("");
    } catch (error) {
      setSessionError(error instanceof Error ? error.message : "Session could not be loaded.");
    }
  }, []);

  useEffect(() => {
    if (needsSession) {
      void refreshSession();
    }
  }, [needsSession, refreshSession]);

  if (needsSession && sessionError) {
    return (
      <main className="auth-shell">
        <section className="auth-panel" role="alert">
          <h1>Session error</h1>
          <p>{sessionError}</p>
          <button className="button primary" type="button" onClick={() => void refreshSession()}>
            Retry
          </button>
        </section>
      </main>
    );
  }

  if (pathname === "/admin") {
    return <Admin session={session} refreshSession={refreshSession} />;
  }

  if (pathname === "/login") {
    return <Login />;
  }

  if (pathname === "/logout") {
    return <Logout session={session} refreshSession={refreshSession} />;
  }

  if (normalizedPathname === "/privacy") {
    return <Privacy />;
  }

  if (normalizedPathname === "/blog") {
    return <PublicSite page="blog" session={session} />;
  }

  if (normalizedPathname === "/tools" || normalizedPathname === "/visualizer") {
    return <ToolsDownload />;
  }

  return <PublicSite page="home" session={session} />;
}
